import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useState } from 'react';
import { PreferredLocale } from '../../constants/cookies';
import { useAreFeatureFlagsReady } from '../../feature-flags/hooks/use-are-feature-flags-ready';
import { useCookie } from '../../hooks/use-cookie-value';
import { GetBrowserLocale } from './get-browser-locale';
import { getShipStationSupportedLocale } from './get-supported-locale';
import { initialAvailableLocaleState } from './initial-state';

import { useUserSettingLocale } from './use-user-setting-locale';
import { LocaleContextProvider } from './';
import { useUserSettingLocaleSync } from './use-user-setting-locale-sync';

const UserSettingLocaleSync = () => {
  useUserSettingLocaleSync();
  return null;
};

export const AuthenticatedLocaleContextProvider = ({ children }) => {
  const { userSettingLocale } = useUserSettingLocale();
  const { cookieValue } = useCookie(PreferredLocale);
  const areFeatureFlagsReady = useAreFeatureFlagsReady();
  const browserDisplayLocale = GetBrowserLocale();

  // NOTE: This variable exists to ensure we can load selected locales before Launch Darkly is available
  const initialAvailableLocaleValues = [
    ...initialAvailableLocaleState,
    ...(getShipStationSupportedLocale(userSettingLocale) ? [getShipStationSupportedLocale(userSettingLocale)] : []),
    ...(getShipStationSupportedLocale(cookieValue) ? [getShipStationSupportedLocale(cookieValue)] : []),
    ...(getShipStationSupportedLocale(browserDisplayLocale) ? [getShipStationSupportedLocale(browserDisplayLocale)] : []),
  ];

  const [availableLocales, setAvailableLocalesState] = useState(initialAvailableLocaleValues);
  const setAvailableLocales = useCallback(
    (locales) => {
      const equals = (array1, array2) => {
        if (array1?.length !== array2?.length) return false;
        return array1?.every((locale) => array2?.includes(locale));
      };

      if (equals(availableLocales, locales)) {
        return;
      }

      setAvailableLocalesState(locales);
    },
    [availableLocales, setAvailableLocalesState],
  );

  useEffect(() => {
    const locales = Array.from(new Set(initialAvailableLocaleState));

    setAvailableLocales(locales);
  }, [availableLocales, areFeatureFlagsReady, setAvailableLocales]);

  return (
    <LocaleContextProvider availableLocales={availableLocales} priorityLocale={userSettingLocale}>
      <UserSettingLocaleSync />
      {children}
    </LocaleContextProvider>
  );
};

AuthenticatedLocaleContextProvider.propTypes = {
  children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]).isRequired,
};
